/**
 * In-memory AI thread cache — the thread survives the PANEL closing
 * (22.08.2026).
 *
 * App renders `<AIChatPanel>` behind `aiPanelOpen &&`, so closing the panel
 * unmounts it. The on-disk store (conversationStore.js) is Tauri-only, its
 * write is debounced 400 ms and skipped while a reply streams — so a close
 * right after a turn, mid-reply, or in a non-Tauri build reopened on an empty
 * transcript. The owner's rule: the thread stays until the user clears the chat
 * or closes the app.
 *
 * This is the first restore source: synchronous, so a reopen shows the thread
 * on the first paint; disk is consulted only when there is nothing here (a
 * fresh launch). Both shapes are kept for the same reason conversationStore
 * keeps both — `messages` is what renders, `anthropicHistory` is what the model
 * sees.
 *
 * ── Lifetime ────────────────────────────────────────────────────────────────
 *
 * Process memory, so closing the app forgets it by nature. Sign-out must forget
 * it too: the disk copy dies with the account root (purgeAccountData), and a
 * teams product must not hand one account's thread — with its tool results —
 * to the next sign-in. Memory has no "where" to lean on, so this module resets
 * itself off the `auth:logout` event, the same session-ended signal authStore
 * dispatches on both the explicit logout() path and the 401-interceptor path.
 *
 * Scoped per workspace, like the disk store.
 */

import { getStorageNamespace } from '../storageNamespace';

/** @type {Map<string, {messages: Array, anthropicHistory: Array}>} */
const threads = new Map();

/**
 * Persistence generation. Bumped on sign-out; a writer that captured an older
 * value is a no-op. This is what makes the cleanup STRUCTURAL rather than an
 * ordering promise: on sign-out `auth:logout` clears this cache, then the UI
 * reset unmounts the panel, and an unmount flush that merely checked "is there
 * a thread?" would write the departed account's thread back — into the cache
 * and onto disk, after the purge. The token is also tied to the storage
 * namespace, so an account switch that never dispatched the event (or a
 * writer that outlived it) cannot land in the next account's bucket either.
 */
let generation = 0;

/** Capture at the moment a thread is produced or restored. */
export function persistToken() {
  return { generation, namespace: getStorageNamespace() };
}

/** True while the session that produced the token is still the current one. */
export function isPersistTokenLive(token) {
  return !!token
    && token.generation === generation
    && token.namespace === getStorageNamespace();
}

function keyFor(workspaceId) {
  const id = String(workspaceId ?? '').trim();
  return id || null;
}

/** The cached thread for a workspace, or null. The arrays are the stored ones. */
export function readCachedConversation(workspaceId) {
  const key = keyFor(workspaceId);
  return key ? threads.get(key) || null : null;
}

/** Replace the cached thread for a workspace. Cheap — no copy, no I/O. */
export function writeCachedConversation(workspaceId, { messages, anthropicHistory }) {
  const key = keyFor(workspaceId);
  if (!key) return;
  threads.set(key, {
    messages: Array.isArray(messages) ? messages : [],
    anthropicHistory: Array.isArray(anthropicHistory) ? anthropicHistory : [],
  });
}

/** Forget one workspace's thread — the panel's Clear action. */
export function clearCachedConversation(workspaceId) {
  const key = keyFor(workspaceId);
  if (key) threads.delete(key);
}

/** Forget everything — sign-out, and tests. Also retires every outstanding token. */
export function clearAllCachedConversations() {
  generation += 1;
  threads.clear();
}

// Same shape as authStore's own module-level listener: both sign-out paths
// end in this dispatch, so one hook covers both.
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', clearAllCachedConversations);
}
